import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { Nav } from '../components/Nav'
import { SessionCard } from '../components/SessionCard'
import { Loading } from '../components/Loading'
import { trackHistoryViewed } from '../lib/analytics'
import type { Session } from '../types'

export default function History() {
  const { get } = useApi()

  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)

  // Filters
  const [emotionFilter, setEmotionFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const loadSessions = useCallback(async (reset = false) => {
    if (reset) {
      setLoading(true)
      setSessions([])
      setCursor(null)
    } else {
      setLoadingMore(true)
    }

    try {
      let url = '/api/sessions?limit=20'
      if (!reset && cursor) {
        url += `&cursor=${cursor}`
      }
      if (emotionFilter) {
        url += `&emotion=${emotionFilter}`
      }

      const data = await get<{
        sessions: Session[]
        pagination: { cursor: string | null; has_more: boolean }
      }>(url)

      if (reset) {
        setSessions(data.sessions)
      } else {
        setSessions(prev => [...prev, ...data.sessions])
      }
      setCursor(data.pagination.cursor)
      setHasMore(data.pagination.has_more)
    } catch (error) {
      console.error('Failed to load sessions:', error)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [get, cursor, emotionFilter])

  // Track page view
  useEffect(() => {
    trackHistoryViewed()
  }, [])

  // Initial load
  useEffect(() => {
    loadSessions(true)
  }, [emotionFilter])

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return

      const scrollTop = window.scrollY
      const windowHeight = window.innerHeight
      const docHeight = document.documentElement.scrollHeight

      if (scrollTop + windowHeight >= docHeight - 200) {
        loadSessions(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [loadSessions, loadingMore, hasMore])

  // Filter sessions by search query (client-side)
  const filteredSessions = searchQuery
    ? sessions.filter(s =>
        s.trigger_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.auto_thought.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.emotion_label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sessions

  const emotions = ['anxious', 'frustrated', 'sad', 'angry', 'overwhelmed', 'worried']

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">History</h1>
      </div>

      <div className="page-content">
        {/* Search */}
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Search sessions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Emotion filter */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <button
            className={`chip ${!emotionFilter ? 'chip-more' : ''}`}
            onClick={() => setEmotionFilter('')}
          >
            All
          </button>
          {emotions.map(emotion => (
            <button
              key={emotion}
              className={`chip ${emotionFilter === emotion ? 'chip-more' : ''}`}
              onClick={() => setEmotionFilter(emotion)}
            >
              {emotion}
            </button>
          ))}
        </div>

        {/* Sessions list */}
        {loading ? (
          <Loading message="Loading sessions..." />
        ) : filteredSessions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <h3 className="empty-state-title">No sessions yet</h3>
            <p className="empty-state-text">
              Start your first reframe to see your history here.
            </p>
            <Link to="/new" className="btn btn-primary">
              Start a Session
            </Link>
          </div>
        ) : (
          <>
            {filteredSessions.map(session => (
              <SessionCard key={session.id} session={session} />
            ))}

            {loadingMore && (
              <Loading message="Loading more..." />
            )}

            {!hasMore && sessions.length > 0 && (
              <p style={{
                textAlign: 'center',
                color: 'var(--color-text-muted)',
                fontSize: '0.875rem',
                padding: '1rem'
              }}>
                You've reached the beginning
              </p>
            )}
          </>
        )}
      </div>

      <Nav />
    </div>
  )
}
